import {I18n} from '@aws-amplify/core';

import {SimplePageHeader} from '@flexinet/ui-components';

import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {ReloadRecordsButton} from '../buttons/ReloadRecordsButton';
import {FilterRecordsButton} from '../buttons/FilterRecordsButton';
import {SendRecordButton} from '../buttons/SendRecordButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {AddRecordButton} from '../buttons/AddRecordButton';

/**
 * The index toolbar, composed in one place so what this module offers is
 * readable at a glance (§2).
 *
 * Order is [Reload, Filter] then Send/Delete/Add — matching the sibling modules
 * in console-app and the customers module next door, which build exactly this
 * shape. The checklist's §2 box is "consistent with siblings", not a fixed
 * canonical order. Send sits before Delete because it is the routine action;
 * the destructive one is last.
 *
 * Every button carries a stable `key`: `tools` is an ARRAY of elements, which
 * is the toolbar contract — not a hand-built <Group> (§2).
 *
 * Buttons are HIDDEN, not disabled, when RBAC forbids the action (§10).
 */
const useTools = (selectedIds: string[], onDeleteComplete?: () => void) => {

    const {allowedActions} = useVerifiedPermissions();

    const tools = [
        <ReloadRecordsButton key='reload'/>,
        <FilterRecordsButton key='filter'/>,
    ];

    if (allowedActions.includes(rbacActions.SendInvoice)) {
        tools.push(
            <SendRecordButton
                key='send'
                selectedIds={selectedIds}
                onSendComplete={onDeleteComplete}
            />,
        );
    }

    if (allowedActions.includes(rbacActions.DeleteInvoice)) {
        tools.push(
            <DeleteRecordButton
                key='delete'
                selectedIds={selectedIds}
                onDeleteComplete={onDeleteComplete}
            />,
        );
    }

    if (allowedActions.includes(rbacActions.CreateInvoice)) {
        tools.push(<AddRecordButton key='add'/>);
    }

    return tools;
};

interface IndexPageHeaderProps {
    selectedIds?: string[];
    onDeleteComplete?: () => void;
}

export const IndexPageHeader = ({
                                    selectedIds = [],
                                    onDeleteComplete,
                                }: IndexPageHeaderProps) => {
    return (
        <SimplePageHeader
            // From I18n, never a bare literal (§2). It was `'Invoices'`.
            title={I18n.get('Invoices')}
            tools={useTools(selectedIds, onDeleteComplete)}
        />
    );
};
